import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useNotify, usePermissions } from 'react-admin';
import { apiPost, apiDownload, ApiClientError, downloadJson } from '../utils';

interface ImportResult {
  success: boolean;
  rowNumber: number;
  displayName: string;
  entityId?: string;
  error?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

export const BulkOperations = () => {
  const notify = useNotify();
  const { permissions } = usePermissions();

  const [activeTab, setActiveTab] = useState(0);

  // Import state
  const [importJson, setImportJson] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState<ImportResult[] | null>(null);
  const [dryRun, setDryRun] = useState(true);

  // Export state
  const [exportEntityType, setExportEntityType] = useState('');
  const [exportCategory, setExportCategory] = useState('');
  const [exportVerificationStatus, setExportVerificationStatus] = useState('');
  const [exportFormat, setExportFormat] = useState<'json' | 'csv'>('json');
  const [exporting, setExporting] = useState(false);

  // Bulk update state
  const [updateEntityIds, setUpdateEntityIds] = useState('');
  const [updateCategory, setUpdateCategory] = useState('');
  const [updateVerificationStatus, setUpdateVerificationStatus] = useState('');
  const [updating, setUpdating] = useState(false);
  const [updateDryRun, setUpdateDryRun] = useState(true);
  const [updatePreview, setUpdatePreview] = useState<{ id: string; display_name: string }[] | null>(null);

  const isSuperAdmin = permissions?.role === 'super_admin';

  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Bulk operations are only available to super administrators.
        </Alert>
      </Box>
    );
  }

  const handleImport = async () => {
    let entities: unknown[];
    try {
      entities = JSON.parse(importJson);
    } catch {
      notify('Invalid JSON format. Please check your input.', { type: 'error' });
      return;
    }

    if (!Array.isArray(entities)) {
      notify('JSON must be an array of entities', { type: 'error' });
      return;
    }

    try {
      setImporting(true);
      setImportResults(null);

      const result = await apiPost<{
        success: boolean;
        summary: { success: number; errors: number };
        results: ImportResult[];
      }>('/admin/api/bulk/import', { entities, dryRun });

      setImportResults(result.results);

      if (result.success) {
        notify(
          dryRun
            ? `Dry run complete: ${result.summary.success} valid, ${result.summary.errors} errors`
            : `Import complete: ${result.summary.success} imported, ${result.summary.errors} errors`,
          { type: result.summary.errors > 0 ? 'warning' : 'success' }
        );
      } else {
        notify(`Import had errors: ${result.summary.errors} failed`, { type: 'warning' });
      }
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Import failed';
      notify(message, { type: 'error' });
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);

    try {
      const exportBody = {
        entityType: exportEntityType || undefined,
        category: exportCategory || undefined,
        verificationStatus: exportVerificationStatus || undefined,
        format: exportFormat,
      };

      const filename = `entities-export-${new Date().toISOString().split('T')[0]}`;

      if (exportFormat === 'csv') {
        await apiDownload('/admin/api/bulk/export', `${filename}.csv`, {
          method: 'POST',
          body: exportBody,
        });
        notify('Export complete', { type: 'success' });
      } else {
        const data = await apiPost<{ count: number; entities: unknown[] }>(
          '/admin/api/bulk/export',
          exportBody
        );

        downloadJson(data.entities, `${filename}.json`);
        notify(`Exported ${data.count} entities`, { type: 'success' });
      }
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Export failed';
      notify(message, { type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleBulkUpdate = async () => {
    const entityIds = updateEntityIds
      .split(/[\n,]/)
      .map((id) => id.trim())
      .filter((id) => id.length > 0);

    if (entityIds.length === 0) {
      notify('Please enter at least one entity ID', { type: 'error' });
      return;
    }

    const updates: Record<string, string> = {};
    if (updateCategory) updates.category = updateCategory;
    if (updateVerificationStatus) updates.verification_status = updateVerificationStatus;

    if (Object.keys(updates).length === 0) {
      notify('Please select at least one field to update', { type: 'error' });
      return;
    }

    setUpdating(true);

    try {
      const result = await apiPost<{
        dryRun: boolean;
        affectedCount: number;
        entities: { id: string; display_name: string }[];
      }>('/admin/api/bulk/update', { entityIds, updates, dryRun: updateDryRun });

      if (updateDryRun) {
        setUpdatePreview(result.entities);
        notify(`Dry run: ${result.affectedCount} entities would be updated`, { type: 'info' });
      } else {
        setUpdatePreview(null);
        notify(`Updated ${result.affectedCount} entities`, { type: 'success' });
      }
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Update failed';
      notify(message, { type: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Bulk Operations
      </Typography>

      <Alert severity="warning" sx={{ mb: 3 }}>
        Bulk operations can affect large amounts of data. Use dry run mode to preview changes before applying them.
      </Alert>

      <Card>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab icon={<UploadIcon />} label="Import" />
          <Tab icon={<DownloadIcon />} label="Export" />
          <Tab icon={<EditIcon />} label="Bulk Update" />
        </Tabs>

        <CardContent>
          {/* Import Tab */}
          <TabPanel value={activeTab} index={0}>
            <Typography variant="h6" gutterBottom>
              Import Entities
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Paste a JSON array of entities to import. Each entity should have display_name, entity_type, and attributes.
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={12}
              value={importJson}
              onChange={(e) => setImportJson(e.target.value)}
              placeholder={`[
  {
    "display_name": "Entity Name",
    "entity_type": "cricket_player",
    "category": "ipl",
    "attributes": {
      "basic": { "name": "..." },
      "career": { ... }
    }
  }
]`}
              sx={{ mb: 2, fontFamily: 'monospace' }}
            />

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={dryRun}
                    onChange={(e) => setDryRun(e.target.checked)}
                  />
                }
                label="Dry run (validate without importing)"
              />
              <Button
                variant="contained"
                startIcon={importing ? <CircularProgress size={20} /> : <UploadIcon />}
                onClick={handleImport}
                disabled={importing || !importJson.trim()}
              >
                {dryRun ? 'Validate' : 'Import'}
              </Button>
            </Box>

            {/* Import Results */}
            {importResults && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Results ({importResults.filter((r) => r.success).length} success, {importResults.filter((r) => !r.success).length} errors)
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importResults.map((result) => (
                        <TableRow key={result.rowNumber}>
                          <TableCell>{result.rowNumber}</TableCell>
                          <TableCell>{result.displayName}</TableCell>
                          <TableCell>
                            {result.success ? (
                              <Chip
                                icon={<CheckCircleIcon />}
                                label="Success"
                                color="success"
                                size="small"
                              />
                            ) : (
                              <Chip
                                icon={<ErrorIcon />}
                                label="Error"
                                color="error"
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {result.entityId || result.error || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </TabPanel>

          {/* Export Tab */}
          <TabPanel value={activeTab} index={1}>
            <Typography variant="h6" gutterBottom>
              Export Entities
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Export entities based on filters. Maximum 10,000 entities per export.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Entity Type</InputLabel>
                  <Select
                    value={exportEntityType}
                    onChange={(e) => setExportEntityType(e.target.value)}
                    label="Entity Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="cricket_player">Cricket Player</MenuItem>
                    <MenuItem value="movie">Movie</MenuItem>
                    <MenuItem value="movie_song">Movie Song</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Category"
                  value={exportCategory}
                  onChange={(e) => setExportCategory(e.target.value)}
                  placeholder="e.g., ipl, tamil"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Verification Status</InputLabel>
                  <Select
                    value={exportVerificationStatus}
                    onChange={(e) => setExportVerificationStatus(e.target.value)}
                    label="Verification Status"
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="unverified">Unverified</MenuItem>
                    <MenuItem value="pending_review">Pending Review</MenuItem>
                    <MenuItem value="verified">Verified</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Format</InputLabel>
                  <Select
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value as 'json' | 'csv')}
                    label="Format"
                  >
                    <MenuItem value="json">JSON</MenuItem>
                    <MenuItem value="csv">CSV</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Button
              variant="contained"
              startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={handleExport}
              disabled={exporting}
            >
              Export
            </Button>
          </TabPanel>

          {/* Bulk Update Tab */}
          <TabPanel value={activeTab} index={2}>
            <Typography variant="h6" gutterBottom>
              Bulk Update Entities
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Update multiple entities at once. Enter entity IDs (one per line or comma-separated).
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label="Entity IDs"
                  value={updateEntityIds}
                  onChange={(e) => setUpdateEntityIds(e.target.value)}
                  placeholder="Paste entity IDs here (one per line or comma-separated)"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Fields to Update
                </Typography>

                <TextField
                  fullWidth
                  size="small"
                  label="Category"
                  value={updateCategory}
                  onChange={(e) => setUpdateCategory(e.target.value)}
                  placeholder="Leave empty to keep current"
                  sx={{ mb: 2 }}
                />

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Verification Status</InputLabel>
                  <Select
                    value={updateVerificationStatus}
                    onChange={(e) => setUpdateVerificationStatus(e.target.value)}
                    label="Verification Status"
                  >
                    <MenuItem value="">Keep Current</MenuItem>
                    <MenuItem value="unverified">Unverified</MenuItem>
                    <MenuItem value="pending_review">Pending Review</MenuItem>
                    <MenuItem value="verified">Verified</MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={updateDryRun}
                      onChange={(e) => setUpdateDryRun(e.target.checked)}
                    />
                  }
                  label="Dry run (preview without updating)"
                />

                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={updating ? <CircularProgress size={20} /> : <EditIcon />}
                    onClick={handleBulkUpdate}
                    disabled={updating || !updateEntityIds.trim()}
                  >
                    {updateDryRun ? 'Preview' : 'Update'}
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {/* Update Preview */}
            {updatePreview && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Preview: {updatePreview.length} entities would be updated
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Name</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {updatePreview.map((entity) => (
                        <TableRow key={entity.id}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {entity.id.substring(0, 8)}...
                          </TableCell>
                          <TableCell>{entity.display_name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
};
